import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import axios from 'axios';
import { 
  ChevronRight, 
  ChevronDown,
  Ribbon,
  AlertTriangle,
  Target,
  Eye,
  Video,
  List,
  Circle
} from 'lucide-react';
import PageWrapper from '../components/PageWrapper';
import Breadcrumbs from '../components/Breadcrumbs';
import VideoCard from '../components/VideoCard';
import UnifiedSearch from '../components/UnifiedSearch';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Cancer types configuration
const cancerTypes = [
  {
    id: 'prostate',
    name: 'Prostate Cancer',
    icon: Circle,
    description: 'Most common cancer in men. Often slow-growing with excellent outcomes when detected early.',
    keywords: ['Prostate Cancer', 'Active Surveillance', 'Prostatectomy vs Radiation'],
    color: '#3b82f6'
  },
  {
    id: 'bladder',
    name: 'Bladder Cancer',
    icon: Circle,
    description: 'Non-muscle invasive, muscle-invasive, BCG therapy, cystectomy, urinary diversions',
    keywords: ['Bladder Cancer', 'Muscle-Invasive', 'BCG', 'TURBT', 'Cystectomy', 'Intravesical', 'Bladder Preservation', 'Trimodal', 'Urinary Diversion', 'Surveillance'],
    color: '#eab308'
  },
  {
    id: 'kidney',
    name: 'Kidney Cancer',
    icon: Circle,
    description: 'Renal cell carcinoma, small renal masses, treatment options',
    keywords: ['Kidney Cancer', 'Renal Cell', 'Small Renal Mass', 'Nephrectomy', 'Metastatic Kidney', 'Ablative', 'Cryoablation', 'Kidney Tumor'],
    color: '#78716c'
  },
  {
    id: 'ureteral',
    name: 'Ureteral & Renal Pelvic Cancer',
    icon: Circle,
    description: 'Upper tract urothelial carcinoma (UTUC) affecting the ureter and renal pelvis',
    keywords: ['Ureteral Cancer', 'Renal Pelvic Cancer', 'Upper Tract Urothelial', 'UTUC', 'Nephroureterectomy'],
    color: '#8b5cf6'
  },
  {
    id: 'testicular',
    name: 'Testicular Cancer',
    icon: Circle,
    description: 'Most common cancer in young men (15-35). Staging, treatment, and RPLND.',
    keywords: ['Testicular Cancer', 'RPLND', 'Retroperitoneal', 'Orchiectomy'],
    color: '#22c55e'
  },
  {
    id: 'penile',
    name: 'Penile Cancer',
    icon: Circle,
    description: 'Rare but treatable. HPV-related, diagnosis, and surgical options.',
    keywords: ['Penile Cancer', 'Penile Carcinoma', 'Penectomy'],
    color: '#f97316'
  },
  {
    id: 'adrenal',
    name: 'Adrenal Tumors',
    icon: Circle,
    description: 'Adrenal masses, pheochromocytoma, adrenocortical carcinoma.',
    keywords: ['Adrenal Tumor', 'Adrenal Mass', 'Pheochromocytoma', 'Adrenocortical', 'Adrenalectomy'],
    color: '#ec4899'
  }
];

const CancersPage = () => {
  const [articles, setArticles] = useState([]);
  const [videos, setVideos] = useState([]);
  const [loading, setLoading] = useState(true);
  const [expandedCategories, setExpandedCategories] = useState(new Set());
  const [showAllExpanded, setShowAllExpanded] = useState(false);

  useEffect(() => {
    const loadContent = async () => {
      try {
        const [articlesRes, videosRes] = await Promise.all([
          axios.get(`${API}/blog?limit=100`),
          axios.get(`${API}/videos?limit=50`)
        ]);
        
        // Filter to only cancer-related articles
        const cancerKeywords = cancerTypes.flatMap(c => c.keywords);
        const filtered = articlesRes.data.filter(article => 
          cancerKeywords.some(keyword => article.title.toLowerCase().includes(keyword.toLowerCase()))
        );
        setArticles(filtered);
        
        // Filter videos for cancer
        const cancerTerms = ['cancer', 'tumor', 'carcinoma', 'oncology', 'malignant', 'bcg', 'turbt', 'cystectomy'];
        const cancerVideos = videosRes.data.filter(v => {
          const category = (v.category || '').toLowerCase();
          const title = v.title.toLowerCase();
          const tags = (v.tags || []).map(t => t.toLowerCase());
          return category === 'cancer' || 
                 cancerTerms.some(term => title.includes(term) || tags.some(t => t.includes(term)));
        });
        setVideos(cancerVideos.slice(0, 6));
      } catch (error) {
        console.error("Error loading content:", error);
      } finally {
        setLoading(false);
      }
    };
    loadContent();
  }, []);

  const categorizeCancer = (article) => {
    for (const type of cancerTypes) {
      if (type.keywords.some(keyword => article.title.toLowerCase().includes(keyword.toLowerCase()))) {
        return type.id;
      }
    }
    return 'other';
  };

  const getArticlesByCategory = (typeId) => {
    return articles.filter(article => categorizeCancer(article) === typeId);
  };

  const toggleCategory = (categoryId) => {
    setExpandedCategories(prev => {
      const newSet = new Set(prev);
      if (newSet.has(categoryId)) {
        newSet.delete(categoryId);
      } else {
        newSet.add(categoryId);
      }
      return newSet;
    });
  };

  const expandAll = () => {
    const allCategoryIds = cancerTypes
      .filter(type => getArticlesByCategory(type.id).length > 0)
      .map(type => type.id);
    setExpandedCategories(new Set(allCategoryIds));
    setShowAllExpanded(true);
  };

  const collapseAll = () => {
    setExpandedCategories(new Set());
    setShowAllExpanded(false);
  };

  // Sort articles alphabetically for Show All
  const sortedArticles = [...articles].sort((a, b) => a.title.localeCompare(b.title));

  return (
    <PageWrapper>
      <Breadcrumbs items={[
        { label: 'Home', path: '/' },
        { label: 'Urologic Cancers', path: '/cancers' }
      ]} />

      <div className="hub-header cancers-header" data-testid="cancers-header">
        <Ribbon size={48} className="hub-icon" />
        <h1>Urologic Cancers</h1>
        <p>Comprehensive information about screening, diagnosis, and treatment options for urologic cancers.</p>
      </div>

      {/* Search Bar */}
      <div className="hub-search-wrapper">
        <UnifiedSearch placeholder="Search cancer information, treatments, tests..." variant="hub" />
      </div>

      {/* Expand/Collapse All Buttons */}
      <div className="accordion-controls" data-testid="accordion-controls">
        <button onClick={expandAll} className="accordion-control-btn">
          Expand All
        </button>
        <button onClick={collapseAll} className="accordion-control-btn">
          Collapse All
        </button>
      </div>

      {/* Accordion List */}
      {loading ? (
        <div className="loading-state">Loading cancer information...</div>
      ) : (
        <div className="conditions-accordion" data-testid="cancers-accordion">
          {/* Show All Accordion */}
          <div className="accordion-section accordion-show-all" data-testid="accordion-show-all">
            <button
              className={`accordion-header ${showAllExpanded ? 'expanded' : ''}`}
              onClick={() => setShowAllExpanded(!showAllExpanded)}
              data-testid="accordion-header-show-all"
            >
              <div className="accordion-header-left">
                <List size={22} className="accordion-icon" />
                <div className="accordion-title-group">
                  <h3>Show All Cancer Articles</h3>
                  <span className="accordion-description">Browse all cancer articles alphabetically</span>
                </div>
              </div>
              <div className="accordion-header-right">
                <span className="accordion-count">{articles.length}</span>
                {showAllExpanded ? (
                  <ChevronDown size={20} className="accordion-chevron" />
                ) : (
                  <ChevronRight size={20} className="accordion-chevron" />
                )}
              </div>
            </button>
            
            {showAllExpanded && (
              <div className="accordion-content accordion-content-all" data-testid="accordion-content-show-all">
                {sortedArticles.map(article => (
                  <Link 
                    to={`/cancers/${article.id}`}
                    key={article.id}
                    className="accordion-article-link"
                  >
                    <span className="article-title">{article.title}</span>
                    <ChevronRight size={16} className="article-arrow" />
                  </Link>
                ))}
              </div>
            )}
          </div>

          {/* Cancer Type Accordions */}
          {cancerTypes.map(type => {
            const IconComponent = type.icon;
            const typeArticles = getArticlesByCategory(type.id);
            const count = typeArticles.length;
            const isExpanded = expandedCategories.has(type.id);
            
            if (count === 0) return null;
            
            return (
              <div key={type.id} className="accordion-section" data-testid={`accordion-${type.id}`}>
                <button
                  className={`accordion-header ${isExpanded ? 'expanded' : ''}`}
                  onClick={() => toggleCategory(type.id)}
                  data-testid={`accordion-header-${type.id}`}
                  style={{ borderLeft: `4px solid ${type.color}` }}
                >
                  <div className="accordion-header-left">
                    <IconComponent size={22} className="accordion-icon" style={{ color: type.color }} />
                    <div className="accordion-title-group">
                      <h3>{type.name}</h3>
                      <span className="accordion-description">{type.description}</span>
                    </div>
                  </div>
                  <div className="accordion-header-right">
                    <span className="accordion-count" style={{ backgroundColor: type.color }}>{count}</span>
                    {isExpanded ? (
                      <ChevronDown size={20} className="accordion-chevron" />
                    ) : (
                      <ChevronRight size={20} className="accordion-chevron" />
                    )}
                  </div>
                </button>
                
                {isExpanded && (
                  <div className="accordion-content" data-testid={`accordion-content-${type.id}`}>
                    {typeArticles.map(article => (
                      <Link 
                        to={`/cancers/${article.id}`}
                        key={article.id}
                        className="accordion-article-link"
                      >
                        <span className="article-title">{article.title}</span>
                        <ChevronRight size={16} className="article-arrow" />
                      </Link>
                    ))}
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}

      {/* Quick Links Section */}
      <div className="cancer-quick-links">
        <h3>Quick Links</h3>
        <div className="quick-links-grid">
          <Link to="/tests/psa-test" className="quick-link-card">
            <Target size={24} />
            <div>
              <h4>PSA Testing</h4>
              <p>Understanding prostate cancer screening</p>
            </div>
          </Link>
          <Link to="/after-visit/elevated-psa" className="quick-link-card">
            <Eye size={24} />
            <div>
              <h4>Elevated PSA Follow-up</h4>
              <p>What happens after an abnormal PSA</p>
            </div>
          </Link>
          <Link to="/after-visit/prostate-biopsy" className="quick-link-card">
            <Target size={24} />
            <div>
              <h4>Prostate Biopsy Aftercare</h4>
              <p>Recovery after prostate biopsy</p>
            </div>
          </Link>
          <Link to="/after-visit/hematuria" className="quick-link-card">
            <AlertTriangle size={24} />
            <div>
              <h4>Blood in Urine Evaluation</h4>
              <p>Workup for hematuria</p>
            </div>
          </Link>
        </div>
      </div>

      {/* Important Notice */}
      <div className="cancer-notice">
        <AlertTriangle size={24} />
        <div>
          <h4>Important Information</h4>
          <p>
            A cancer diagnosis can be overwhelming. These resources are meant to help you understand 
            your options and have informed conversations with your care team. Every case is unique, 
            and your urologist will recommend the best approach for your specific situation.
          </p>
        </div>
      </div>

      {/* Cancer Videos Section */}
      {videos.length > 0 && (
        <section className="hub-videos-section" data-testid="cancers-videos">
          <div className="hub-videos-header">
            <h2><Video size={24} /> Cancer-Related Videos</h2>
            <Link to="/videos?category=cancer" className="view-all-link">
              View All Cancer Videos <ChevronRight size={16} />
            </Link>
          </div>
          <div className="hub-videos-grid">
            {videos.map(video => (
              <VideoCard key={video.id} video={video} compact={true} />
            ))}
          </div>
        </section>
      )}

      <div className="hub-disclaimer">
        <p>
          <strong>Medical Disclaimer:</strong> This information is for educational purposes only 
          and is not a substitute for professional medical advice. Always consult your physician 
          or oncologist for guidance specific to your situation.
        </p>
      </div>
    </PageWrapper>
  );
};

export default CancersPage;
